import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../AuthContext.jsx';
import { api } from '../api.js';
import TrendChart from '../components/TrendChart.jsx';
import CalendarView from '../components/CalendarView.jsx';
import SolveList from '../components/SolveList.jsx';
import { EVENTS, eventByKey } from '@shared/events.js';
import { formatTime } from '@shared/format.js';

export default function MyData() {
  const { user } = useAuth();
  const [event, setEvent] = useState(user?.main_event || '3x3');
  const [range, setRange] = useState('30d');
  const [trend, setTrend] = useState(null);
  const [overview, setOverview] = useState(null);
  const [summary, setSummary] = useState([]);
  const [calendar, setCalendar] = useState({ month: '', days: {} });
  const [solves, setSolves] = useState([]);
  const [loading, setLoading] = useState(true);

  const now = new Date();
  const localMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, o, s, c, list] = await Promise.all([
        api(`/stats/trend?event=${event}&range=${range}`),
        api(`/stats/overview?event=${event}`),
        api('/stats/summary'),
        api(`/stats/calendar?event=${event}&month=${localMonth}`),
        api(`/solves?event=${event}&limit=100`),
      ]);
      setTrend(t.trend);
      setOverview(o.overview);
      setSummary(s.events);
      setCalendar({ month: c.month, days: c.days });
      setSolves(list.solves);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [event, range]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteSolve = async (solve) => {
    try {
      await api(`/solves/${solve.id}`, { method: 'DELETE' });
      load();
    } catch {
      /* ignore */
    }
  };

  const ev = eventByKey(event);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-100">我的数据</h1>
          <p className="text-sm text-slate-500 mt-0.5">成绩趋势 · 打卡日历 · 个人纪录</p>
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          {EVENTS.map((e) => (
            <button
              key={e.key}
              onClick={() => setEvent(e.key)}
              className={`tab ${event === e.key ? 'tab-active' : 'tab-idle'}`}
            >
              {e.name}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="text-center text-sm text-slate-500 py-16">加载中…</div>
      ) : (
        <>
          {/* 项目统计卡 */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
            <Stat label="总成绩" value={overview?.total_solves ?? 0} suffix="把" />
            <Stat label="单次 PB" value={overview?.pb_single != null ? formatTime(overview.pb_single) : '—'} accent />
            <Stat label="PB Ao5" value={overview?.pb_ao5 != null ? formatTime(overview.pb_ao5) : '—'} />
            <Stat label="PB Ao12" value={overview?.pb_ao12 != null ? formatTime(overview.pb_ao12) : '—'} />
            <Stat label="当前 Ao5" value={overview?.current_ao5 != null ? formatTime(overview.current_ao5) : '—'} />
            <Stat label="连续打卡" value={overview?.streak ?? 0} suffix="天" />
          </div>

          {/* 成长曲线 */}
          <div className="card p-5">
            <h2 className="font-semibold text-slate-100 mb-4">{ev.name} 成长曲线</h2>
            <TrendChart trend={trend} range={range} onRangeChange={setRange} />
          </div>

          <div className="grid lg:grid-cols-2 gap-5">
            {/* 打卡日历 */}
            <div className="card p-5">
              <h2 className="font-semibold text-slate-100 mb-4">打卡日历</h2>
              <CalendarView days={calendar.days} month={calendar.month} />
            </div>

            {/* 各项目纪录总表 */}
            <div className="card p-5">
              <h2 className="font-semibold text-slate-100 mb-4">各项目纪录</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-slate-500">
                      <th className="py-2 pr-2 font-medium">项目</th>
                      <th className="py-2 pr-2 font-medium text-right">总成绩</th>
                      <th className="py-2 pr-2 font-medium text-right">单次 PB</th>
                      <th className="py-2 pr-2 font-medium text-right">Ao5</th>
                      <th className="py-2 pr-2 font-medium text-right">Ao12</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/40">
                    {summary.map((s) => (
                      <tr key={s.event} className={s.event === event ? 'text-accent' : 'text-slate-300'}>
                        <td className="py-2.5 pr-2">
                          {EVENTS.find((e) => e.key === s.event)?.name || s.event}
                        </td>
                        <td className="py-2.5 text-right font-mono">{s.total_solves}</td>
                        <td className="py-2.5 text-right font-mono">{s.pb_single != null ? formatTime(s.pb_single) : '—'}</td>
                        <td className="py-2.5 text-right font-mono">{s.pb_ao5 != null ? formatTime(s.pb_ao5) : '—'}</td>
                        <td className="py-2.5 text-right font-mono">{s.pb_ao12 != null ? formatTime(s.pb_ao12) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* 成绩历史 */}
          <div className="card p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-100">最近 100 把成绩</h2>
              <span className="text-xs text-slate-500">可删除误记成绩</span>
            </div>
            <SolveList solves={solves} onDelete={deleteSolve} />
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, suffix = '', accent = false }) {
  return (
    <div className="card px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`mt-1 font-mono text-lg font-semibold ${accent ? 'text-accent' : 'text-slate-100'}`}>
        {value}{suffix}
      </div>
    </div>
  );
}
